import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Fish } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";

export default function Chatbot() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("alikama_chat_session") || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: settings.chatbot_welcome }]);
    }
  }, [open, settings.chatbot_welcome, messages.length]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const userMsg = text ?? input.trim();
    if (!userMsg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: userMsg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chatbot/chat", { session_id: sessionId, message: userMsg });
      if (!sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem("alikama_chat_session", data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `Sorry, I'm having trouble. Please call us at ${settings.phone}.` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chatbot-toggle"
        aria-label="Open chat"
        className="fixed right-4 bottom-24 md:right-6 md:bottom-28 z-50 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white rounded-full w-14 h-14 flex items-center justify-center shadow-2xl transition transform hover:-translate-y-0.5"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed right-4 bottom-44 md:right-6 md:bottom-48 z-50 w-[92vw] max-w-sm h-[65vh] max-h-[560px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-[color:var(--color-border)] fade-in"
        >
          <div className="bg-[color:var(--color-primary)] text-white px-4 py-3 flex items-center gap-3">
            <div className="bg-[color:var(--color-accent)] rounded-full w-9 h-9 flex items-center justify-center">
              <Fish className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-display text-lg leading-none">Kama</div>
              <div className="text-xs text-white/70">{settings.business_name} assistant</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="chatbot-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[color:var(--color-bg)] no-scrollbar" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={m.role === "user" ? "chat-bubble-user" : "chat-bubble-bot"}>{m.content}</div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start"><div className="chat-bubble-bot">Kama is typing…</div></div>
            )}
            <div ref={bottomRef} />
          </div>

          {messages.length <= 1 && (
            <div className="px-3 py-2 flex flex-wrap gap-2 border-t border-[color:var(--color-border)] bg-white">
              {(settings.chatbot_quick_replies || []).map((q, i) => (
                <button
                  key={i}
                  onClick={() => send(q)}
                  data-testid={`chatbot-quick-${i}`}
                  className="text-xs border border-[color:var(--color-border)] rounded-full px-3 py-1 hover:bg-[color:var(--color-primary)] hover:text-white transition"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 p-3 border-t border-[color:var(--color-border)] bg-white"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about products, orders, classes…"
              data-testid="chatbot-input"
              className="flex-1 text-sm px-3 py-2 border border-[color:var(--color-border)] rounded-md focus:outline-none focus:border-[color:var(--color-primary)]"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="chatbot-send"
              className="bg-[color:var(--color-accent)] hover:bg-[color:var(--color-accent-hover)] text-white p-2 rounded-md disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
